import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { ActionBar } from '../../../components/ActionBar'
import { Alert, AlertVariants } from '../../../components/Alert'
import { Button, ButtonVariants } from '../../../components/Button'
import { Card } from '../../../components/Card'
import {
  ConnectGoogleButton,
  DisconnectGoogleDialog,
  GoogleCalendarPicker,
  GoogleLogo,
  Spinner,
} from '../../../components/GoogleCalendar'
import { useGoogleCalendarStatus } from '../../../hooks/useGoogleCalendar'

export const Route = createFileRoute('/_authenticated/settings/calendar')({
  component: CalendarSettings,
})

function CalendarSettings() {
  const navigate = useNavigate()
  const { data: status, isLoading, isError, refetch } = useGoogleCalendarStatus()
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false)

  const statusByCalendarId = useMemo(
    () => new Map((status?.calendars ?? []).map((cal) => [cal.calendarId, cal])),
    [status],
  )

  const backButton = (
    <button className="inline-block cursor-pointer" onClick={() => navigate({ to: '/settings' })}>
      <ChevronBackward className="size-6" />
    </button>
  )

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Spinner className="size-8 border-4 text-primary-500" />
        </div>
      )
    }

    if (isError || !status) {
      return (
        <div className="flex flex-col gap-4">
          <Alert type={AlertVariants.error} title="Couldn't load your calendar connection">
            Please try again in a moment.
          </Alert>
          <Button intent={ButtonVariants.default} onClick={() => refetch()} className="self-start">
            Try again
          </Button>
        </div>
      )
    }

    if (!status.connected) {
      return (
        <div className="flex flex-col gap-4">
          <p>
            Connect Google Calendar to see your appointments alongside your goals and habits, and to
            show the events you plan in Zendia in Google too. Events read from Google are read-only
            in Zendia: edit them in Google Calendar.
          </p>
          <ConnectGoogleButton returnUrl="/settings/calendar" className="self-start" />
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-neutral-500/30 opacity-60">
            <span className="font-medium">Microsoft Outlook</span>
            <span className="text-xs uppercase tracking-wide">Coming soon</span>
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <GoogleLogo />
          <div className="flex-1">
            <p className="font-medium">Google Calendar connected</p>
            <p className="text-sm text-neutral-500" role="status" aria-live="polite">
              {status.syncInProgress ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner className="size-3" /> Syncing...
                </span>
              ) : (
                'Choose which calendars Zendia reads from Google and which it writes to Google.'
              )}
            </p>
          </div>
        </div>

        <GoogleCalendarPicker
          returnUrl="/settings/calendar"
          statusByCalendarId={statusByCalendarId}
        />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-neutral-500/30">
          <div>
            <p className="font-medium">Disconnect Google Calendar</p>
            <p className="text-sm text-neutral-500">
              Stop syncing, choose whether to delete the Google calendars Zendia created, and remove
              Zendia's access to your Google account.
            </p>
          </div>
          <Button
            intent={ButtonVariants.negative}
            onClick={() => setIsDisconnectOpen(true)}
            className="self-start sm:self-auto"
          >
            Disconnect
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <ActionBar title="Calendar" startAction={backButton} />
      <div className="flex flex-col p-4 gap-4 w-full max-w-2xl mx-auto">
        <Card>{renderBody()}</Card>
      </div>
      {/* Outside the connected view: the dialog stays open after disconnecting, when it has to
          report Google calendars it could not delete */}
      <DisconnectGoogleDialog open={isDisconnectOpen} onClose={() => setIsDisconnectOpen(false)} />
    </>
  )
}
